import { format, formatDistanceToNow } from "date-fns";

/**
 * Parses a date input, treating slash dates as the backend's DD/MM/YYYY.
 *
 * The API stores project dates as DD/MM/YYYY strings; `new Date(string)`
 * would interpret them as US MM/DD/YYYY (silently swapping day and month
 * whenever the day is <= 12, e.g. "03/06/2026" June 3rd -> March 6th).
 * Everything else (ISO strings, Date objects, timestamps) parses unchanged.
 */
export const parseBackendDate = (
  date: string | Date | null | undefined
): Date | null => {
  if (!date) return null;

  let dateObj: Date;
  if (typeof date === 'string') {
    const ddmmyyyy = date.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s|$)/);
    if (ddmmyyyy) {
      const [, d, m, y] = ddmmyyyy;
      dateObj = new Date(Number(y), Number(m) - 1, Number(d));
      // reject impossible dates like 31/02 (Date would roll them over)
      if (dateObj.getDate() !== Number(d) || dateObj.getMonth() !== Number(m) - 1) {
        return null;
      }
    } else {
      dateObj = new Date(date);
    }
  } else {
    dateObj = date;
  }

  return isNaN(dateObj.getTime()) ? null : dateObj;
};

/**
 * Safely formats a date string or Date object
 * @param date - The date to format (string, Date, or null/undefined)
 * @param formatString - The format string for date-fns
 * @param fallback - Fallback text if date is invalid
 * @returns Formatted date string or fallback
 */
export const safeFormatDate = (
  date: string | Date | null | undefined,
  formatString: string,
  fallback: string = "No date available"
): string => {
  try {
    const dateObj = parseBackendDate(date);
    if (!dateObj) return fallback;

    return format(dateObj, formatString);
  } catch (error) {
    console.warn('Error formatting date:', error);
    return fallback;
  }
};

/**
 * Safely formats a date distance (e.g., "2 days ago")
 * @param date - The date to format (string, Date, or null/undefined)
 * @param options - Options for formatDistanceToNow
 * @param fallback - Fallback text if date is invalid
 * @returns Formatted distance string or fallback
 */
export const safeFormatDistance = (
  date: string | Date | null | undefined,
  options?: { addSuffix?: boolean },
  fallback: string = "N/A"
): string => {
  try {
    const dateObj = parseBackendDate(date);
    if (!dateObj) return fallback;

    return formatDistanceToNow(dateObj, options);
  } catch (error) {
    console.warn('Error formatting date distance:', error);
    return fallback;
  }
};

/**
 * Safely creates a Date object
 * @param date - The date input (string, Date, or null/undefined)
 * @returns Date object or null if invalid
 */
export const safeCreateDate = (
  date: string | Date | null | undefined
): Date | null => {
  try {
    return parseBackendDate(date);
  } catch (error) {
    console.warn('Error creating date object:', error);
    return null;
  }
};
